import React, { useEffect } from "react";
import { InputNumber } from "antd";
import { MinusOutlined, PlusOutlined } from "@ant-design/icons";
import PreviewQuestionLabel from "../../../common/PreviewQuestionLabel/PreviewQuestionLabel";
import { useSurveyInputStore } from "../../../../surveyStore";
import useEvaluateConstraint from "../../question-preview/hooks/useEvaluateConstraint";
import PreviewQuestionConstraints from "../../../common/PreviewQuestionConstraints/PreviewQuestionConstraints";

import "./Number.scss";

export const Number = React.memo(
   ({ props, hasErrors, surveyId, userId, submissionId, label, hint, defaultValue }) => {
      const { id, questionType, isRequired, answers } = props;

      const inputState = useSurveyInputStore((state) => state.inputs?.[id]);
      const setInputs = useSurveyInputStore((state) => state.setInputs);
      const isThousandSep = props?.appearance;
      const hasCalulation = props?.calculation;

      const hasSubmissionId = Boolean(submissionId);

      const submissionAnswer = hasSubmissionId
         ? answers?.find((answer) =>
              props.repeatInstanceIndex
                 ? answer?.answerDetail.repeatGroup === props.repeatInstanceIndex
                 : answer
           )?.answerDetail?.number
         : null;

      const value = hasSubmissionId ? submissionAnswer : inputState?.answerDetail?.number ?? null;

      const passConstraint = useEvaluateConstraint(
         !hasSubmissionId ? props.constraint : null,
         value
      );

      const showErrors =
         hasErrors && isRequired && (!inputState || inputState?.answerDetail?.number === null);

      const onChange = (e) => {
         if (submissionId) {
            return;
         }
         if (e !== 0 && !e) {
            setInputs({
               target: {
                  name: id,
                  value: ""
               }
            });
         } else {
            setInputs({
               target: {
                  name: id,
                  value: {
                     surveyId: surveyId,
                     userId: userId,
                     questionId: id,
                     questionType: questionType,
                     name: props.name,
                     skipLogicAnswer: e,
                     answerDetail: {
                        number: e
                     }
                  }
               }
            });
         }
      };

      const onKeyDown = (event) => {
         if (isThousandSep) {
            const allowedKeys = [
               "Backspace",
               "Delete",
               "ArrowLeft",
               "ArrowRight",
               "Tab",
               "Enter",
               "."
            ];
            const isNumber = /^[0-9]$/.test(event.key);
            const isAllowed = isNumber || allowedKeys.includes(event.key);

            if (
               event.key === "." &&
               (event.currentTarget.value.includes(".") || event.currentTarget.selectionStart === 0)
            ) {
               event.preventDefault();
            }

            if (!isAllowed) {
               event.preventDefault();
            }
         } else {
            if (event.key === "." || event.key === ",") {
               event.preventDefault();
            }
         }
      };

      useEffect(() => {
         if (defaultValue || defaultValue === 0) {
            onChange(defaultValue);
         }
         // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [defaultValue]);

      const defaultValueCalc = hasSubmissionId ? submissionAnswer : value;

      return (
         <div className="number-submission-type-wrapper">
            <PreviewQuestionLabel label={label} isRequired={isRequired} hint={hint} />
            <InputNumber
               size="large"
               type={isThousandSep ? "text" : "number"}
               stringMode={isThousandSep ? true : false}
               formatter={(value) => {
                  if (!value) return "";
                  const [integer, decimal] = `${value}`.split(".");
                  const formattedInteger = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                  return isThousandSep
                     ? decimal !== undefined
                        ? `${formattedInteger}.${decimal}`
                        : formattedInteger
                     : value;
               }}
               parser={(value) => (isThousandSep ? value?.replace(/,/g, "") ?? "" : value)}
               name={id}
               step={1}
               onKeyDown={onKeyDown}
               readOnly={hasSubmissionId || hasCalulation}
               status={showErrors || (passConstraint !== null && !passConstraint) ? "error" : ""}
               placeholder={"Type your answer here"}
               value={defaultValueCalc}
               onChange={onChange}
               controls={{
                  downIcon: <MinusOutlined style={{ fontSize: 16 }} />,
                  upIcon: <PlusOutlined style={{ fontSize: 16 }} />
               }}
               prefixCls="number-submission-type-wrapper--number"
            />

            <span className="number-submission-type-wrapper--error">
               {showErrors ? "This field is required" : " "}
               {passConstraint !== null && !passConstraint ? (
                  <PreviewQuestionConstraints constraintMessages={props.constraintMessages} />
               ) : null}
            </span>
         </div>
      );
   }
);
